import { supabase } from '@/integrations/supabase/client';
import { indexedDBService } from '@/services/indexedDBService';

export interface OrganizationData {
  id: string;
  organization_name: string;
  admin_user_id: string;
  status: string;
}

export interface StorageValidationResult {
  isConnected: boolean;
  error?: string;
  folderName?: string;
  connectionType?: 'personal' | 'organization';
  organizationId?: string;
}

export const blockDatabaseWrites = async (): Promise<boolean> => {
  try {
    // Check if user is super admin
    const { data, error } = await supabase.rpc('is_super_admin');
    if (error) {
      console.error('Error checking super admin status:', error);
      return true; // Block writes on error
    }
    // Allow writes only for super admins
    return !data;
  } catch (error) {
    console.error('Error in blockDatabaseWrites:', error);
    return true; // Block writes on error
  }
};

export const validateStorageConnection = async (): Promise<StorageValidationResult> => {
  try {
    console.log('StorageIsolation: Validating IndexedDB connection');
    
    // IndexedDB is always available
    const initResult = await indexedDBService.initialize();
    
    return {
      isConnected: initResult.success,
      connectionType: 'personal' // Default to personal for now
    };
  } catch (error) {
    console.error('StorageIsolation: Validation error:', error);
    return {
      isConnected: false,
      error: error instanceof Error ? error.message : 'IndexedDB validation failed'
    };
  }
};

export const reconnectStorage = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('StorageIsolation: IndexedDB is always connected');
    return { success: true };
  } catch (error) {
    console.error('StorageIsolation: Reconnect error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to reconnect IndexedDB'
    };
  }
};

export const requestStoragePermission = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('StorageIsolation: IndexedDB permissions are automatic');
    return { success: true };
  } catch (error) {
    console.error('StorageIsolation: Permission request error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to request IndexedDB permission'
    };
  }
};

export const createPersonalStorageConnection = async (config: any): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('StorageIsolation: IndexedDB is always available');
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // IndexedDB is always initialized
    const initResult = await indexedDBService.initialize();

    if (!initResult.success) {
      throw new Error(initResult.error || 'Failed to initialize storage');
    }

    // Save storage connection to database
    const { error: dbError } = await supabase
      .from('storage_connections')
      .insert({
        user_id: user.id,
        connection_type: 'personal',
        storage_config: {
          type: 'indexeddb',
          provider: 'local',
          setupDate: new Date().toISOString()
        },
        is_active: true
      });

    if (dbError) throw dbError;

    // Update user profile with storage config
    const { error: profileError } = await supabase
      .from('user_profiles')
      .update({
        storage_config: {
          type: 'indexeddb',
          provider: 'local'
        },
        storage_connected: true,
        requires_storage_setup: false
      })
      .eq('user_id', user.id);

    if (profileError) throw profileError;

    console.log('StorageIsolation: Personal storage connection created successfully');
    return { success: true };
  } catch (error) {
    console.error('Failed to create personal storage connection:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create personal storage connection'
    };
  }
};

export const createOrganizationStorageConnection = async (
  organizationId: string, 
  config: any
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Initialize IndexedDB service
    const initResult = await indexedDBService.initialize();

    if (!initResult.success) {
      throw new Error(initResult.error || 'Failed to initialize organization storage');
    }

    // Test the connection by creating organization data
    const testData = {
      id: crypto.randomUUID(),
      organizationId,
      appName: 'GreenCard ESG - Organization',
      version: '1.0.0',
      initialized: new Date().toISOString(),
      adminUserId: user.id
    };

    const testResult = await indexedDBService.add('reports', testData);
    if (!testResult.success) {
      throw new Error(testResult.error || 'Failed to create organization structure');
    }

    // Save storage connection to database
    const { error: dbError } = await supabase
      .from('storage_connections')
      .insert({
        user_id: user.id,
        organization_id: organizationId,
        connection_type: 'organization',
        storage_config: {
          type: 'indexeddb',
          provider: 'local',
          setupDate: new Date().toISOString()
        },
        is_active: true
      });

    if (dbError) throw dbError;

    return { success: true };
  } catch (error) {
    console.error('Failed to create organization storage connection:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create organization storage connection'
    };
  }
};

export const getUserOrganizations = async (): Promise<{ organizations: OrganizationData[]; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get organizations where user is admin
    const { data: adminOrgs, error: adminError } = await supabase
      .from('organizations')
      .select('*')
      .eq('admin_user_id', user.id);

    if (adminError) throw adminError;

    // Get organizations where user is member
    const { data: memberOrgs, error: memberError } = await supabase
      .from('organization_members')
      .select(`
        organizations(*)
      `)
      .eq('user_id', user.id)
      .eq('status', 'active');

    if (memberError) throw memberError;

    const allOrgs = [
      ...(adminOrgs || []),
      ...(memberOrgs?.map(m => m.organizations).filter(Boolean) || [])
    ];

    // Remove duplicates
    const uniqueOrgs = allOrgs.reduce((acc, org) => {
      if (!acc.find(o => o.id === org.id)) {
        acc.push(org);
      }
      return acc;
    }, [] as OrganizationData[]);

    return { organizations: uniqueOrgs };
  } catch (error) {
    console.error('Failed to get user organizations:', error);
    return { 
      organizations: [],
      error: error instanceof Error ? error.message : 'Failed to get organizations' 
    };
  }
};

export const initializeStorageOnLogin = async (): Promise<{ success: boolean; needsPermission?: boolean; error?: string }> => {
  try {
    console.log('StorageIsolation: Initializing storage on login');
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    // Check if user has storage configuration in database
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('storage_config, storage_connected')
      .eq('user_id', user.id)
      .maybeSingle();

    if (profileError && profileError.code !== 'PGRST116') {
      console.error('StorageIsolation: Error fetching user profile:', profileError);
      // Don't fail completely, just log the error
    }

    // If no storage config in database, IndexedDB is still available
    if (!profile?.storage_config) {
      console.log('StorageIsolation: No storage configuration found, using IndexedDB');
      return { success: true, needsPermission: false };
    }

    // Initialize IndexedDB service
    const initResult = await indexedDBService.initialize();

    if (!initResult.success) {
      console.log('StorageIsolation: Storage init failed, but continuing:', initResult.error);
      // Don't fail completely, storage might work later
    }

    console.log('StorageIsolation: Storage initialized successfully');
    return { success: true, needsPermission: false };
  } catch (error) {
    console.error('StorageIsolation: Error initializing storage on login:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to initialize storage'
    };
  }
};

export const getOrganizationStorageConfig = async (organizationId: string): Promise<{
  success: boolean;
  config?: any;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('organizations')
      .select('storage_config')
      .eq('id', organizationId)
      .single();

    if (error) throw error;

    return { success: true, config: data?.storage_config };
  } catch (error) {
    console.error('Failed to get organization storage config:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to get storage config' 
    };
  }
};

export const shareStorageWithTeamMember = async (
  organizationId: string,
  memberUserId: string,
  permissions: { read: boolean; write: boolean; delete: boolean }
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Create a storage connection for the team member
    const { error } = await supabase
      .from('storage_connections')
      .insert({
        user_id: memberUserId,
        organization_id: organizationId,
        connection_type: 'organization',
        storage_config: {
          shared: true,
          permissions,
          shared_at: new Date().toISOString()
        },
        is_active: true
      });

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Failed to share storage with team member:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to share storage' 
    };
  }
};

export const getCollaborativeData = async (
  organizationId: string,
  dataType: string
): Promise<{ success: boolean; data?: any[]; error?: string }> => {
  try {
    // Get organization storage config
    const storageResult = await getOrganizationStorageConfig(organizationId);
    if (!storageResult.success || !storageResult.config) {
      throw new Error('No storage configuration found for organization');
    }

    // Initialize IndexedDB service
    const initResult = await indexedDBService.initialize();

    if (!initResult.success) {
      throw new Error(initResult.error || 'Failed to initialize collaborative storage');
    }

    // Load collaborative data from reports store
    const dataResult = await indexedDBService.getAll('reports');
    if (!dataResult.success) {
      // Return empty array if no data exists yet
      return { success: true, data: [] };
    }

    // Filter by organization and data type
    const filteredData = dataResult.data?.filter(item => 
      item.organizationId === organizationId && 
      item.type === dataType
    ) || [];

    return { success: true, data: filteredData };
  } catch (error) {
    console.error('Failed to get collaborative data:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to load collaborative data' 
    };
  }
};

export const saveCollaborativeData = async (
  organizationId: string,
  dataType: string,
  data: any
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Get organization storage config
    const storageResult = await getOrganizationStorageConfig(organizationId);
    if (!storageResult.success || !storageResult.config) {
      throw new Error('No storage configuration found for organization');
    }

    // Initialize IndexedDB service
    const initResult = await indexedDBService.initialize();

    if (!initResult.success) {
      throw new Error(initResult.error || 'Failed to initialize collaborative storage');
    }

    // Create new collaborative data entry
    const newData = {
      id: crypto.randomUUID(),
      organizationId,
      type: dataType,
      data,
      created_at: new Date().toISOString(),
      created_by: (await supabase.auth.getUser()).data.user?.id
    };

    // Save to reports store
    const saveResult = await indexedDBService.add('reports', newData);
    if (!saveResult.success) {
      throw new Error(saveResult.error || 'Failed to save collaborative data');
    }

    return { success: true };
  } catch (error) {
    console.error('Failed to save collaborative data:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to save collaborative data' 
    };
  }
};

export const getUserOrganizationRole = async (): Promise<{
  success: boolean;
  role?: string;
  organizationId?: string;
  permissions?: any;
  error?: string;
}> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('organization_members')
      .select(`
        role,
        organization_id,
        permissions
      `)
      .eq('user_id', user.id)
      .eq('status', 'active')
      .single();

    if (error && error.code !== 'PGRST116') throw error;

    return { 
      success: true, 
      role: data?.role,
      organizationId: data?.organization_id,
      permissions: data?.permissions
    };
  } catch (error) {
    console.error('Failed to get user organization role:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to get organization role' 
    };
  }
};

export const checkCollaborativeAccess = async (
  organizationId: string,
  requiredPermission: 'read' | 'write' | 'delete'
): Promise<{ success: boolean; hasAccess: boolean; error?: string }> => {
  try {
    const roleResult = await getUserOrganizationRole();
    if (!roleResult.success) {
      return { success: false, hasAccess: false, error: roleResult.error };
    }

    // Check if user belongs to the organization
    if (roleResult.organizationId !== organizationId) {
      return { success: true, hasAccess: false };
    }

    // Check role-based permissions
    const role = roleResult.role;
    const permissions = roleResult.permissions;

    switch (role) {
      case 'admin':
        return { success: true, hasAccess: true };
      case 'editor':
        return { success: true, hasAccess: requiredPermission !== 'delete' };
      case 'viewer':
        return { success: true, hasAccess: requiredPermission === 'read' };
      default:
        // Check custom permissions if available
        if (permissions && permissions[requiredPermission] === true) {
          return { success: true, hasAccess: true };
        }
        return { success: true, hasAccess: false };
    }
  } catch (error) {
    console.error('Failed to check collaborative access:', error);
    return { 
      success: false, 
      hasAccess: false, 
      error: error instanceof Error ? error.message : 'Failed to check access' 
    };
  }
};
